import { Injectable } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { throwError } from 'rxjs';
import { Payment } from 'src/app/models/payment';
import { BaseCardService } from '../base-card.service';
import { InvoiceService } from '../invoice/invoice.service';
import { LocationService } from '../location/location.service';
import { ProviderService } from '../provider/provider.service';
import { ScriptService } from '../script.service';
import { TransactionService } from '../transaction/transaction.service';

export enum TokenErrorReasonCode {
  CardHolder = 'INVALID_CARD_HOLDER',
  CardNumber = 'INVALID_CARD_NUMBER',
  CardExpiration = 'INVALID_CARD_EXPIRATION',

  // TODO: works with GP team to validate normalizing
  CardCVVPortico = 'INVALID_CARD_CVV',
  CardCVVTransIT = 'INVALID_CARD_SECURITY_CODE',
}

@Injectable({
  providedIn: 'root',
})
export class CreditCardService extends BaseCardService {
  constructor(
    private scriptService: ScriptService,
    private providerService: ProviderService,
    protected invoiceService: InvoiceService,
    locationService: LocationService,
    transactionService: TransactionService
  ) {
    super(invoiceService, locationService, transactionService);
  }

  public getInitialPayment = (amount: string): Payment => {
    const invoice = this.invoiceService.invoice;

    return new Payment({
      RequestedAmount: amount,
      CardholderName: `${invoice.firstName} ${invoice.lastName}`,
      StreetAddress1: invoice?.address1?.slice(0, 100) ?? '',
      StreetAddress2: invoice?.address2?.slice(0, 100) ?? '',
      // approved = false for city length > 20
      City: invoice?.city?.slice(0, 20) ?? '',
      StateCode: invoice?.stateName ?? '',
      PostalCode: invoice?.zip ?? '',
    });
  };

  public async getCreditCardForm(cardholderName: string): Promise<any> {
    const GlobalPayments = await this.configureGPLibrary();
    return Promise.resolve(
      GlobalPayments.ui.form(this.createCardFormInstance(cardholderName))
    );
  }

  public setPaymentDataBeforeTransaction = (
    paymentData: Payment,
    resp: any
  ) => {
    return {
      ...paymentData,
      transactionId: this.invoiceService.login.transactionId,
      CountryCode: this.locationService.location.countryCode,
      CardType: resp.details.cardType,
      PaymentReference: resp.paymentReference,
      RedactedInfo: resp.details.cardNumber,
      PaymentTypeId: 100000,
      CardMonthExpiration: resp.details.expiryMonth,
      CardYearExpiration: resp.details.expiryYear,
      CardholderName: resp.details.cardholderName,
      CardPIN: '',
    };
  };

  private async configureGPLibrary(): Promise<any> {
    const GlobalPayments = await this.scriptService.loadGlobalPaymentsLibrary();

    GlobalPayments.configure({
      deviceId:
        this.providerService.provider?.publicProviderConfiguration
          .DeviceId, //TransIT
      manifest:
        this.providerService.provider?.publicProviderConfiguration
          .Manifest, //TransIT
      publicApiKey:
        this.providerService.provider?.publicProviderConfiguration
          .SecureSubmitPublicKey, //Portico
      env: this.providerService.provider?.publicProviderConfiguration
        .Environment,
      accessToken: this.providerService.provider?.publicProviderConfiguration
        .AccessToken, //GPAPI
      merchantId: this.providerService.provider?.publicProviderConfiguration
        .MerchantId, //GPAPI
      merchantManagementAccountId: this.providerService.provider?.publicProviderConfiguration
        .MerchantManagementAccountId, //GPAPI
      apiVersion: '2021-03-22', ////GPAPI
    });

    GlobalPayments.on('error', () => {
      throwError;
    });

    return Promise.resolve(GlobalPayments);
  }

  private createCardFormInstance = (cardholderName: string) => {
    return {
      fields: {
        'card-number': {
          placeholder: '•••• •••• •••• ••••',
          target: '#iframesCardNumber',
        },
        'card-expiration': {
          label: 'Expiration',
          placeholder: 'MM / YYYY',
          target: '#iframesCardExpiration',
        },
        'card-cvv': {
          label: 'Security Code',
          placeholder: '•••',
          target: '#iframesCardCvv',
        },
        'card-holder-name': {
          placeholder: 'Jane Smith',
          target: '#iframesCardHolderName',
          value: cardholderName,
        },
        submit: {
          target: '#iframesSubmit',
          text: translate('Pay'),
        },
      },
      styles: {
        input: {
          color: '#333333',
          background: '#fff',
          border: '1px solid',
          'border-color': '#bbb3b9 #c7c1c6 #c7c1c6',
          'box-sizing': 'border-box',
          'font-family': 'serif',
          'font-size': '13px',
          'font-weight': '800',
          'line-height': '16px',
          margin: '0 .5em 0 0',
          'max-width': '100%',
          outline: '0',
          padding: '13px 10px',
          'vertical-align': 'baseline',
          height: '44px',
          width: '100%',
        },
        '#secure-payment-field': {
          'font-family': 'sans-serif',
          'font-weight': '800',
          'box-sizing': 'border-box',
          display: 'block',
          height: '44px',
          padding: '13px 10px',
          'font-size': '13px',
          'line-height': '16px',
          color: '#333333',
          'background-color': '#fff',
          border: '1px solid #ccc',
          'border-radius': '0px',
          '-webkit-box-shadow': 'inset 0 1px 1px rgba(0,0,0,.075)',
          'box-shadow': 'inset 0 1px 1px rgba(0,0,0,.075)',
          '-webkit-transition':
            'border-color ease-in-out .15s,-webkit-box-shadow ease-in-out .15s',
          '-o-transition':
            'border-color ease-in-out .15s,box-shadow ease-in-out .15s',
          transition:
            'border-color ease-in-out .15s,box-shadow ease-in-out .15s',
          width: '100%',
        },
        '#secure-payment-field.submit': {
          'background-color': '#36b46e',
          'font-family': 'sans-serif',
          'text-transform': 'uppercase',
          color: '#ffffff',
          border: '0px solid transparent',
        },
        '#secure-payment-field.submit:focus': {
          color: '#ffffff',
          'background-color': '#258851',
          outline: 'none',
        },
        '#secure-payment-field.submit:hover': {
          'background-color': '#258851',
        },
        '#secure-payment-field-wrapper #secure-payment-field:focus': {
          border: '1px solid #3989e3',
          outline: 'none',
          'box-shadow': 'none',
        },
        'secure-payment-field-wrapper #secure-payment-field': {
          height: '44px',
        },
        'button.submit': {
          'box-sizing': 'border-box',
          display: 'inline-block',
          padding: '6px 12px',
          'margin-bottom': '0',
          'font-size': '14px',
          'font-weight': '400',
          'line-height': '1.42857143',
          'text-align': 'center',
          'white-space': 'nowrap',
          'vertical-align': 'middle',
          '-ms-touch-action': 'manipulation',
          'touch-action': 'manipulation',
          cursor: 'pointer',
          '-webkit-user-select': 'none',
          '-moz-user-select': 'none',
          '-ms-user-select': 'none',
          'user-select': 'none',
          'background-image': 'none',
          border: '1px solid transparent',
          'border-radius': '4px',
          color: '#fff',
          'background-color': '#4BAF6D',
          'border-color': '#2e6da4',
        },
        '#secure-payment-field.card-cvv': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/cvv1.png?raw=true) no-repeat right !important',
          'background-size': '36px 25px !important',
          'background-position': 'calc(100% - 5px) !important',
        },
        'input#secure-payment-field[name=cardNumber]': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-inputcard-blank@2x.png?raw=true) no-repeat right',
          'background-size': '28px 21px',
          'background-position': 'calc(100% - 5px)',
          'z-index': '9999',
        },
        '#secure-payment-field.invalid.card-type-visa': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-visa@2x.png?raw=true) no-repeat right',
          'background-size': '83px 88px',
          'background-position-y': '-44px',
        },
        '#secure-payment-field.valid.card-type-visa': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-visa@2x.png?raw=true) no-repeat right top',
          'background-size': '82px 86px',
        },
        '#secure-payment-field.invalid.card-type-discover': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-discover@2x.png?raw=true) no-repeat right',
          'background-size': '85px 90px',
          'background-position-y': '-44px',
        },
        '#secure-payment-field.valid.card-type-discover': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-discover@2x.png?raw=true) no-repeat right',
          'background-size': '85px 90px',
          'background-position-y': '1px',
        },
        '#secure-payment-field.invalid.card-type-amex': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-savedcards-amex@2x.png?raw=true) no-repeat right',
          'background-size': '50px 90px',
          'background-position-y': '-44px',
        },
        '#secure-payment-field.valid.card-type-amex': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-savedcards-amex@2x.png?raw=true) no-repeat right top',
          'background-size': '50px 90px',
        },
        '#secure-payment-field.invalid.card-type-mastercard': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-mastercard.png?raw=true) no-repeat right',
          'background-size': '62px 105px',
          'background-position-y': '-60px',
        },
        '#secure-payment-field.valid.card-type-mastercard': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-mastercard.png?raw=true) no-repeat right',
          'background-size': '62px 105px',
          'background-position-y': '-9px',
        },
        '#secure-payment-field.invalid.card-type-jcb': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-jcb@2x.png?raw=true) no-repeat right',
          'background-size': '55px 94px',
          'background-position-y': '-44px',
        },
        '#secure-payment-field.valid.card-type-jcb': {
          background:
            'transparent url(https://github.com/hps/heartland-php/blob/master/examples/end-to-end/assets/images/ss-saved-jcb@2x.png?raw=true) no-repeat right top',
          'background-size': '55px 94px',
          'background-position-y': '2px',
        },
        '#secure-payment-field.invalid': {
          border: '1px solid #D13636 !important',
        },
        'input#secure-payment-field[name=cardNumber]::-ms-clear': {
          display: 'none',
        },
      },
    };
  };
}
